/**
 * Link — React (web) implementation.
 *
 * Renders a native `<a>` so keyboard activation, middle-click, "open in new
 * tab" and `mailto:` all behave as browser links. All styling references the
 * constants in Link.ts; hover and focus need pseudo-classes, so they are drawn
 * by one injected stylesheet rather than inline styles.
 */

import React, { useId } from 'react';
import { Icon } from '../Icon/Icon.web';
import { ensureFocusVisibleStyle, ensureStyle } from '../../elements/focusStyle.web';
import {
  LINK_COLOR,
  LINK_HOVER_UNDERLINE_THICKNESS,
  LINK_ICON_COLOR,
  LINK_ICON_GAP,
  LINK_ICON_SIZE,
  LINK_NEW_TAB_ICON,
  LINK_NEW_TAB_REL,
  LINK_NEW_TAB_TARGET,
  LINK_RADIUS,
  LINK_TYPOGRAPHY,
  isSafeLinkHref,
  type LinkProps,
} from './Link';

export interface WebLinkProps extends LinkProps {
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  'data-testid'?: string;
}

const LINK_FOCUS_STYLE_ID = 'galapagos-link-focus-visible';
const LINK_HOVER_STYLE_ID = 'galapagos-link-hover';
const LINK_SELECTOR = '[data-galapagos-link="true"]';

function ensureLinkHoverStyle(): void {
  // Hover only where a pointer can actually hover, so a tap on a touch
  // screen does not leave the link stuck in its hover state.
  ensureStyle(
    LINK_HOVER_STYLE_ID,
    `@media (hover: hover) { ${LINK_SELECTOR}:hover { text-decoration-thickness: ${LINK_HOVER_UNDERLINE_THICKNESS}px; } }`,
  );
}

const VISUALLY_HIDDEN: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

export function Link({
  label,
  href,
  variant = 'inline',
  opensNewTab = false,
  newTabLabel,
  accessibilityLabel,
  testID,
  actionId = 'ui.link.open',
  onClick,
  'data-testid': dataTestId,
}: WebLinkProps) {
  ensureFocusVisibleStyle(LINK_FOCUS_STYLE_ID, [LINK_SELECTOR]);
  ensureLinkHoverStyle();
  const newTabId = useId();
  // An unsafe href (e.g. `javascript:`) renders as a link with no target.
  const safeHref = isSafeLinkHref(href) ? href : undefined;
  // An aria-label replaces the name the hidden new-tab text would join, so
  // that text is kept as the link's description instead.
  const describeNewTab = Boolean(accessibilityLabel && opensNewTab && newTabLabel);
  const font = LINK_TYPOGRAPHY[variant];

  const style: React.CSSProperties = {
    // A plain link stays `inline` so it can wrap inside body copy; only the
    // new-tab glyph needs a flex row to sit beside the label.
    display: opensNewTab ? 'inline-flex' : 'inline',
    alignItems: 'center',
    gap: LINK_ICON_GAP,
    position: 'relative',
    borderRadius: LINK_RADIUS,
    color: LINK_COLOR,
    textDecorationLine: 'underline',
    fontFamily: font.fontFamily,
    fontWeight: font.fontWeight,
    fontSize: font.fontSize,
    lineHeight: `${font.lineHeight}px`,
    letterSpacing: font.letterSpacing,
  };

  return (
    <a
      href={safeHref}
      target={opensNewTab ? LINK_NEW_TAB_TARGET : undefined}
      rel={opensNewTab ? LINK_NEW_TAB_REL : undefined}
      aria-label={accessibilityLabel}
      aria-describedby={describeNewTab ? newTabId : undefined}
      onClick={onClick}
      style={style}
      data-galapagos-link="true"
      data-analytics-action={actionId}
      data-testid={dataTestId ?? testID}
    >
      {label}
      {opensNewTab ? (
        <>
          <Icon
            name={LINK_NEW_TAB_ICON}
            size={LINK_ICON_SIZE}
            color={LINK_ICON_COLOR}
            aria-hidden
          />
          {newTabLabel ? (
            <span id={newTabId} style={VISUALLY_HIDDEN}>
              {newTabLabel}
            </span>
          ) : null}
        </>
      ) : null}
    </a>
  );
}
